import React, { useEffect, useMemo, useState } from "react";
import { ShoppingBag, Search } from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ShopProductCard from "@/components/ShopProductCard";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import EiceSection from "@/components/EiceSection";
import { useCart } from "@/context/CartContext";

const EICE_CATEGORY = "École Internationale des Chantres";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [eiceFormations, setEiceFormations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("Tous");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const { count, openCart } = useCart();

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/shop/categories").then((r) => setCategories((r.data.categories || []).filter((c) => c !== EICE_CATEGORY))).catch(() => {});
    api.get("/products").then((r) => setEiceFormations(r.data.filter((p) => p.category === EICE_CATEGORY && p.format !== "Atelier"))).catch(() => {});
  }, []);

  const tabs = ["Tous", ...categories, EICE_CATEGORY];

  // On-site EICE ateliers now come from the PayPal shop catalog.
  const eiceAteliers = useMemo(
    () => products.filter((p) => p.category === EICE_CATEGORY),
    [products]
  );

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.category !== EICE_CATEGORY);
    if (category !== "Tous" && category !== EICE_CATEGORY) list = list.filter((p) => p.category === category);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || (p.description || "").toLowerCase().includes(s));
    }
    const arr = [...list];
    if (sort === "price_asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") arr.sort((a, b) => b.price - a.price);
    else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [products, category, q, sort]);

  return (
    <div>
      <Seo
        title="Boutique — Livres, E-books & Musique numériques"
        description="Boutique officielle du Prophète Steeve Orphôss : livres numériques, e-books, musique et ressources spirituelles à télécharger."
        path="/boutique"
      />
      <PageHeader overline="Boutique officielle" title="Boutique officielle" subtitle="Ressources numériques à télécharger pour fortifier votre foi." />

      <section className="py-14 md:py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Toolbar */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 mb-8">
            <div className="flex flex-wrap gap-3">
              {tabs.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  data-testid={`shop-category-${c.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    category === c ? "bg-gold text-white shadow-md shadow-gold/25" : "bg-white text-brown-black/70 border border-gold/25 hover:border-gold hover:text-gold"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={openCart}
              data-testid="shop-view-cart-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors self-start lg:self-auto"
            >
              <ShoppingBag size={16} /> Voir le panier
              {count > 0 && <span className="flex items-center justify-center w-5 h-5 text-[11px] font-bold text-brown-dark bg-gold-light rounded-full">{count}</span>}
            </button>
          </div>

          {category !== EICE_CATEGORY && (
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <div className="relative flex-1">
                <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-brown-black/40" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un produit…"
                  data-testid="shop-search"
                  className="w-full pl-11 pr-4 py-3 rounded-full border border-gold/25 bg-white focus:border-gold focus:outline-none transition-colors"
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="shop-sort"
                className="px-4 py-3 rounded-full border border-gold/25 bg-white focus:border-gold focus:outline-none transition-colors text-sm font-semibold text-brown-dark"
              >
                <option value="new">Nouveautés</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
              </select>
            </div>
          )}

          {category === EICE_CATEGORY ? (
            <EiceSection formations={eiceFormations} ateliers={eiceAteliers} />
          ) : filtered.length === 0 ? (
            <p className="text-center text-brown-black/50 py-16" data-testid="shop-empty">Aucun produit ne correspond à votre recherche.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.1}>
                  <ShopProductCard product={p} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
